import React from 'react';
import { AlertTriangle, ArrowRight, Loader, X } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

/** Reservasjon slik backend beskriver den i RESERVED_STOCK_FOR_MOVE (camelCase). */
export interface ReservedStockMoveReservation {
  resID: number;
  quantity: number;
  pListID?: number | null;
  orderNo: number;
  orderNoERP?: string | null;
}

/** `details` fra RESERVED_STOCK_FOR_MOVE (StcTransOrderController.ValidateReservedStockForMoveAsync). */
export interface ReservedStockMoveDetails {
  itemNo: string;
  fromBinCode: string;
  toBinCode?: string | null;
  requestedQuantity: number;
  freeQuantity: number;
  neededQuantity: number;
  selectedReservations: ReservedStockMoveReservation[];
}

interface ReservedStockMoveModalProps {
  details: ReservedStockMoveDetails;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * «Flytt med reservasjon» (TW 36427814): flyttingen tar av saldo som er reservert for plukk.
 * Operatøren ser hvilke ordrer som berøres og bekrefter at reservasjonene og plukklinjene
 * flyttes med til ny lokasjon.
 */
export const ReservedStockMoveModal: React.FC<ReservedStockMoveModalProps> = ({ details, busy, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const toBin = details.toBinCode ?? '';

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={busy ? undefined : onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="reserved-move-title"
          className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl sm:max-w-lg flex flex-col"
        >
          <div className="flex items-center justify-between p-4 sm:p-5 border-b">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
              <h2 id="reserved-move-title" className="text-base sm:text-lg font-semibold">
                {t('warehouse.modules.warehouseTransfer.reservedMove.title', {}, 'Varen er reservert for plukk')}
              </h2>
            </div>
            <button
              onClick={onCancel}
              disabled={busy}
              aria-label={t('common.buttons.close', {}, 'Lukk')}
              className="p-2 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px] flex items-center justify-center flex-shrink-0"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 sm:p-5 space-y-4 overflow-y-auto">
            <p className="text-sm text-gray-700">
              {t('warehouse.modules.warehouseTransfer.reservedMove.intro',
                { '0': details.neededQuantity, '1': details.requestedQuantity, '2': details.itemNo, '3': details.fromBinCode, '4': details.freeQuantity },
                `${details.neededQuantity} av ${details.requestedQuantity} stk ${details.itemNo} på ${details.fromBinCode} er reservert for plukk (ledig: ${details.freeQuantity}).`)}
            </p>

            <ul className="border rounded-lg divide-y">
              {details.selectedReservations.map(r => (
                <li key={r.resID} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <div className="font-medium">
                      {t('warehouse.modules.warehouseTransfer.reservedMove.order', { '0': r.orderNoERP || r.orderNo }, `Ordre ${r.orderNoERP || r.orderNo}`)}
                    </div>
                    {r.pListID != null && (
                      <div className="text-xs text-gray-500">
                        {t('warehouse.modules.warehouseTransfer.reservedMove.pickList', { '0': r.pListID }, `Plukkliste ${r.pListID}`)}
                      </div>
                    )}
                  </div>
                  <span className="font-mono whitespace-nowrap">{r.quantity} {t('warehouse.modules.warehouseTransfer.reservedMove.pcs', {}, 'stk')}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="font-mono">{details.fromBinCode}</span>
              <ArrowRight className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="font-mono">{toBin}</span>
            </div>

            <p className="text-xs text-gray-600">
              {t('warehouse.modules.warehouseTransfer.reservedMove.explain', { '0': toBin },
                `Reservasjonene og plukklinjene flyttes med til ${toBin} når flyttingen avsluttes. Plukkeren henter da varen på ny lokasjon.`)}
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-5 border-t">
            <button
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-3 sm:py-2 border border-gray-300 rounded font-medium hover:bg-gray-50 min-h-[44px]"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={onConfirm}
              disabled={busy}
              className="px-4 py-3 sm:py-2 rounded font-medium bg-cyan-600 text-white hover:bg-cyan-700 min-h-[44px] flex items-center justify-center gap-2"
            >
              {busy && <Loader className="animate-spin h-4 w-4" />}
              {t('warehouse.modules.warehouseTransfer.reservedMove.confirm', {}, 'Flytt med reservasjon')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
